import React, { useState, useEffect } from 'react';
import {
    useDataProvider,
    useNotify,
    useTranslate,
    useRecordContext,
} from 'react-admin';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Tooltip,
    InputAdornment,
    useMediaQuery,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Add,
    Delete,
    ContentCopy,
    VpnKey,
    Warning,
    CheckCircle,
    Info,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { NumberField } from '../common/NumberField';

interface ApiKey {
    id: string;
    suffix: string;
    description: string | null;
    createdAt: string;
    expiresAt: string | null;
    lastUsedAt: string | null;
    isActive: boolean;
    isExpired: boolean;
    createdBy: {
        id: string;
        email: string;
        name: string;
    } | null;
}

// "Never expires" is stored as a sentinel ~100 years in the future. The backend
// still records a real expiry timestamp so RLS / index logic stays simple; the
// UI detects anything more than 50 years out as "Never".
const NEVER_EXPIRE_DAYS = 36525;
const NEVER_EXPIRE_THRESHOLD_MS = 50 * 365 * 24 * 60 * 60 * 1000;

const isNeverExpire = (expiresAt: string | null | undefined): boolean => {
    if (!expiresAt) return true;
    return new Date(expiresAt).getTime() - Date.now() > NEVER_EXPIRE_THRESHOLD_MS;
};

type ExpiryOption = '7' | '30' | '60' | '90' | '180' | '365' | 'never' | 'custom';

export const ApiKeysManagement: React.FC = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
    const [generatedClientId, setGeneratedClientId] = useState<string>('');
    const [generatedClientSecret, setGeneratedClientSecret] = useState<string>('');
    const [description, setDescription] = useState('');
    const [expiryOption, setExpiryOption] = useState<ExpiryOption>('90');
    const [customDays, setCustomDays] = useState<number | ''>('');
    const [generating, setGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [revokeTargetKeyId, setRevokeTargetKeyId] = useState<string | null>(null);
    const [revoking, setRevoking] = useState(false);

    const vendorId = record?.id;

    // Load API keys
    const loadApiKeys = async () => {
        if (!vendorId) return;

        try {
            setLoading(true);
            const response = await dataProvider.getList('vendors', {
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'ASC' },
                filter: {},
                meta: {
                    path: `vendors/${vendorId}/api-keys`,
                    method: 'GET'
                }
            });

            setApiKeys(response.data as unknown as ApiKey[]);
        } catch (error: any) {
            console.error('Failed to load API keys:', error);
            notify(
                translate('resources.vendors.apiKeys.loadFailed') ||
                'Failed to load OAuth 2.0 credentials',
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadApiKeys();
    }, [vendorId]);

    // Resolve the dropdown choice to a concrete number-of-days payload.
    // - preset days → its numeric value
    // - "never" → NEVER_EXPIRE_DAYS sentinel
    // - "custom" → the value the user typed (or undefined if empty/invalid)
    const resolveExpiresInDays = (): number | undefined => {
        if (expiryOption === 'never') return NEVER_EXPIRE_DAYS;
        if (expiryOption === 'custom') return typeof customDays === 'number' ? customDays : undefined;
        return parseInt(expiryOption, 10);
    };

    // Generate new API key
    const handleGenerate = async () => {
        if (!vendorId) return;

        const expiresInDays = resolveExpiresInDays();
        if (expiryOption === 'custom' && expiresInDays === undefined) {
            notify(
                translate('resources.vendors.apiKeys.generateDialog.customRequired') ||
                'Please enter a custom expiry in days, or pick another option',
                { type: 'warning' }
            );
            return;
        }

        try {
            setGenerating(true);
            const response = await dataProvider.create('vendors', {
                data: {
                    description: description || undefined,
                    expiresInDays,
                },
                meta: {
                    path: `vendors/${vendorId}/api-keys`,
                    method: 'POST'
                }
            });

            const responseData = response.data as any;
            setGeneratedClientId(responseData.clientId);
            setGeneratedClientSecret(responseData.clientSecret);
            setGenerateDialogOpen(false);
            setNewKeyDialogOpen(true);
            setDescription('');
            setExpiryOption('90');
            setCustomDays('');

            // Reload keys list
            await loadApiKeys();

            notify(
                translate('resources.vendors.apiKeys.generateSuccess') ||
                'OAuth 2.0 credentials generated successfully',
                { type: 'success' }
            );
        } catch (error: any) {
            console.error('Failed to generate API key:', error);
            notify(
                translate('resources.vendors.apiKeys.generateFailed') ||
                'Failed to generate OAuth 2.0 credentials',
                { type: 'error' }
            );
        } finally {
            setGenerating(false);
        }
    };

    // Open the confirmation dialog for a specific key
    const handleRevoke = (keyId: string) => {
        setRevokeTargetKeyId(keyId);
        setRevokeDialogOpen(true);
    };

    // Actually perform the revoke after the user confirms in the dialog
    const confirmRevoke = async () => {
        if (!vendorId || !revokeTargetKeyId) return;

        try {
            setRevoking(true);
            await dataProvider.delete('vendors', {
                id: revokeTargetKeyId,
                previousData: { id: revokeTargetKeyId } as any,
                meta: {
                    path: `vendors/${vendorId}/api-keys/${revokeTargetKeyId}`,
                    method: 'DELETE'
                }
            });

            notify(
                translate('resources.vendors.apiKeys.revokeSuccess') ||
                'OAuth 2.0 credentials revoked successfully',
                { type: 'success' }
            );
            setRevokeDialogOpen(false);
            setRevokeTargetKeyId(null);
            await loadApiKeys();
        } catch (error: any) {
            console.error('Failed to revoke API key:', error);
            notify(
                translate('resources.vendors.apiKeys.revokeFailed') ||
                'Failed to revoke OAuth 2.0 credentials',
                { type: 'error' }
            );
        } finally {
            setRevoking(false);
        }
    };

    // Copy credentials to clipboard
    const handleCopyCredentials = () => {
        const credentials = `Client ID: ${generatedClientId}\nClient Secret: ${generatedClientSecret}`;
        navigator.clipboard.writeText(credentials);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        notify(
            translate('resources.vendors.apiKeys.newKeyDialog.copied') ||
            'OAuth 2.0 credentials copied to clipboard',
            { type: 'info' }
        );
    };

    const handleCopyClientSecret = () => {
        navigator.clipboard.writeText(generatedClientSecret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        notify(
            translate('resources.vendors.apiKeys.clientSecretCopied') ||
            'Client secret copied to clipboard',
            { type: 'info' }
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header with Generate button */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 2 }, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.vendors.apiKeys.description') ||
                     'API keys allow vendors to authenticate and use the charging API.'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setGenerateDialogOpen(true)}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                        backgroundColor: '#00C853',
                        '&:hover': {
                            backgroundColor: '#00A843',
                        },
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: isMobile ? 'auto' : undefined,
                        alignSelf: { xs: 'flex-start', sm: 'auto' },
                    }}
                >
                    {isMobile
                        ? (translate('resources.vendors.apiKeys.generate') || 'Generate')
                        : (translate('resources.vendors.apiKeys.generate') || 'Generate API Key')}
                </Button>
            </Box>

            {/* API Keys Table / Mobile Cards */}
            {apiKeys.length === 0 ? (
                <Alert severity="info" icon={<Info />}>
                    {translate('resources.vendors.apiKeys.noKeys') ||
                     'No OAuth 2.0 credentials generated yet. Click "Generate Credentials" to create one.'}
                </Alert>
            ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {apiKeys.map((key) => (
                        <Box key={key.id} sx={{ p: 1.5, border: '1px solid #E0E0E0', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <VpnKey sx={{ fontSize: 16, color: '#757575' }} />
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {'\u2022\u2022\u2022'}{key.suffix}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {key.isExpired ? (
                                        <Chip label={translate('resources.vendors.apiKeys.status.expired') || 'Expired'} size="small" icon={<Warning />} sx={{ backgroundColor: '#FFEBEE', color: '#C62828', fontWeight: 600, height: 24, '& .MuiChip-icon': { fontSize: 14 } }} />
                                    ) : key.isActive ? (
                                        <Chip label={translate('resources.vendors.apiKeys.status.active') || 'Active'} size="small" icon={<CheckCircle />} sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, height: 24, '& .MuiChip-icon': { fontSize: 14 } }} />
                                    ) : (
                                        <Chip label={translate('resources.vendors.apiKeys.status.revoked') || 'Revoked'} size="small" sx={{ backgroundColor: '#F5F5F5', color: '#757575', fontWeight: 600, height: 24 }} />
                                    )}
                                    <IconButton size="small" onClick={() => handleRevoke(key.id)} disabled={!key.isActive || key.isExpired}
                                        sx={{ color: '#D32F2F', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                            {key.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {key.description}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.vendors.apiKeys.fields.created') || 'Created'}: {format(new Date(key.createdAt), 'MMM d, yyyy')}
                                </Typography>
                                {key.lastUsedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.vendors.apiKeys.fields.lastUsed') || 'Last Used'}: {format(new Date(key.lastUsedAt), 'MMM d, yyyy')}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.vendors.apiKeys.fields.expires') || 'Expires'}: {isNeverExpire(key.expiresAt)
                                        ? (translate('resources.vendors.apiKeys.neverExpires') || 'Never')
                                        : format(new Date(key.expiresAt as string), 'MMM d, yyyy')}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.suffix') || 'Key'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.description') || 'Description'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.status') || 'Status'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.created') || 'Created'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.lastUsed') || 'Last Used'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.expires') || 'Expires'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {translate('resources.vendors.apiKeys.fields.actions') || 'Actions'}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {apiKeys.map((key) => (
                                <TableRow key={key.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <VpnKey sx={{ fontSize: 18, mr: 1, color: '#757575' }} />
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {'\u2022\u2022\u2022'}{key.suffix}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {key.description || <span style={{ color: '#9E9E9E', fontStyle: 'italic' }}>{translate('resources.vendors.apiKeys.noDescription') || 'No description'}</span>}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {key.isExpired ? (
                                            <Chip
                                                label={translate('resources.vendors.apiKeys.status.expired') || 'Expired'}
                                                size="small"
                                                icon={<Warning />}
                                                sx={{
                                                    backgroundColor: '#FFEBEE',
                                                    color: '#C62828',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        ) : key.isActive ? (
                                            <Chip
                                                label={translate('resources.vendors.apiKeys.status.active') || 'Active'}
                                                size="small"
                                                icon={<CheckCircle />}
                                                sx={{
                                                    backgroundColor: '#E8F5E9',
                                                    color: '#2E7D32',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label={translate('resources.vendors.apiKeys.status.revoked') || 'Revoked'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#F5F5F5',
                                                    color: '#757575',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                            {format(new Date(key.createdAt), 'MMM d, yyyy')}
                                        </Typography>
                                        {key.createdBy && (
                                            <Typography variant="caption" color="text.secondary">
                                                by {key.createdBy.name || key.createdBy.email}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {key.lastUsedAt ? (
                                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                {format(new Date(key.lastUsedAt), 'MMM d, yyyy HH:mm')}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Never used
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {isNeverExpire(key.expiresAt) ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                {translate('resources.vendors.apiKeys.neverExpires') || 'Never'}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                {format(new Date(key.expiresAt as string), 'MMM d, yyyy')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate('resources.vendors.apiKeys.revokeTooltip') || 'Revoke Key'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRevoke(key.id)}
                                                    disabled={!key.isActive || key.isExpired}
                                                    sx={{
                                                        color: '#D32F2F',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Generate API Key Dialog */}
            <Dialog
                open={generateDialogOpen}
                onClose={() => !generating && setGenerateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {translate('resources.vendors.apiKeys.generateDialog.title') || 'Generate New OAuth 2.0 Credentials'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label={translate('resources.vendors.apiKeys.fields.description') || 'Description'}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            placeholder="e.g., Production integration key"
                            helperText={translate('resources.vendors.apiKeys.generateDialog.descriptionHelp') ||
                                      'Optional: Describe what this key will be used for'}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="expiration-select-label">
                                {translate('resources.vendors.apiKeys.fields.expiration') || 'Expiration'}
                            </InputLabel>
                            <Select<ExpiryOption>
                                labelId="expiration-select-label"
                                label={translate('resources.vendors.apiKeys.fields.expiration') || 'Expiration'}
                                value={expiryOption}
                                onChange={(e) => setExpiryOption(e.target.value as ExpiryOption)}
                            >
                                <MenuItem value="7">{translate('resources.vendors.apiKeys.expiration.days7') || '7 days'}</MenuItem>
                                <MenuItem value="30">{translate('resources.vendors.apiKeys.expiration.days30') || '30 days'}</MenuItem>
                                <MenuItem value="60">{translate('resources.vendors.apiKeys.expiration.days60') || '60 days'}</MenuItem>
                                <MenuItem value="90">{translate('resources.vendors.apiKeys.expiration.days90') || '90 days'}</MenuItem>
                                <MenuItem value="180">{translate('resources.vendors.apiKeys.expiration.days180') || '180 days'}</MenuItem>
                                <MenuItem value="365">{translate('resources.vendors.apiKeys.expiration.days365') || '1 year (365 days)'}</MenuItem>
                                <MenuItem value="never">{translate('resources.vendors.apiKeys.expiration.never') || 'No expiration'}</MenuItem>
                                <MenuItem value="custom">{translate('resources.vendors.apiKeys.expiration.custom') || 'Custom…'}</MenuItem>
                            </Select>
                        </FormControl>
                        {expiryOption === 'custom' && (
                            <NumberField
                                label={translate('resources.vendors.apiKeys.fields.expiresInDays') || 'Expires In (Days)'}
                                value={typeof customDays === 'number' ? customDays : undefined}
                                onValueChange={(v) => setCustomDays(v === undefined ? '' : v)}
                                integer
                                fullWidth
                                placeholder="e.g., 120"
                                inputProps={{ min: 1, max: NEVER_EXPIRE_DAYS }}
                                helperText={translate('resources.vendors.apiKeys.generateDialog.customExpiresHelp') ||
                                          'Enter a number of days (1 or more)'}
                            />
                        )}
                        {expiryOption === 'never' && (
                            <Alert severity="info" icon={<Info />}>
                                {translate('resources.vendors.apiKeys.generateDialog.neverExpireWarning') ||
                                 'Keys that never expire should be rotated manually. Make sure you trust the recipient.'}
                            </Alert>
                        )}
                        <Alert severity="warning" icon={<Warning />}>
                            {translate('resources.vendors.apiKeys.generateDialog.warning') ||
                             'The API key will be shown only once. Make sure to copy and save it securely.'}
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setGenerateDialogOpen(false)}
                        disabled={generating}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('ra.action.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleGenerate}
                        variant="contained"
                        disabled={generating}
                        startIcon={generating ? <CircularProgress size={16} /> : <Add />}
                        sx={{
                            backgroundColor: '#00C853',
                            '&:hover': {
                                backgroundColor: '#00A843',
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {generating
                            ? (translate('resources.vendors.apiKeys.generating') || 'Generating...')
                            : (translate('resources.vendors.apiKeys.generate') || 'Generate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Show Generated Key Dialog */}
            <Dialog
                open={newKeyDialogOpen}
                onClose={() => setNewKeyDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: '#00C853', color: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircle sx={{ mr: 1 }} />
                        {translate('resources.vendors.apiKeys.newKeyDialog.title') || 'OAuth 2.0 Credentials Generated Successfully'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="error" icon={<Warning />}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                {translate('resources.vendors.apiKeys.newKeyDialog.important') ||
                                 'IMPORTANT: This is the only time you will see the client secret!'}
                            </Typography>
                            <Typography variant="body2">
                                {translate('resources.vendors.apiKeys.newKeyDialog.saveNow') ||
                                 'Copy and save it now. You will not be able to retrieve it later.'}
                            </Typography>
                        </Alert>

                        <TextField
                            label={translate('resources.vendors.apiKeys.newKeyDialog.clientId') || 'Client ID'}
                            value={generatedClientId}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                sx: {
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    backgroundColor: '#F5F5F5',
                                },
                            }}
                        />

                        <TextField
                            label={translate('resources.vendors.apiKeys.newKeyDialog.clientSecret') || 'Client Secret'}
                            value={generatedClientSecret}
                            fullWidth
                            multiline
                            rows={2}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleCopyClientSecret} edge="end">
                                            <ContentCopy />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    backgroundColor: '#F5F5F5',
                                },
                            }}
                        />

                        {copied && (
                            <Alert severity="success">
                                {translate('resources.vendors.apiKeys.newKeyDialog.copied') || 'Credentials copied to clipboard!'}
                            </Alert>
                        )}

                        <Alert severity="info" icon={<Info />}>
                            <Typography variant="body2">
                                {translate('resources.vendors.apiKeys.newKeyDialog.usage') ||
                                 'Share these OAuth 2.0 credentials with the vendor securely. They will use them to obtain bearer tokens for API authentication.'}
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCopyCredentials}
                        variant="outlined"
                        startIcon={<ContentCopy />}
                        sx={{ textTransform: 'none', mr: 1 }}
                    >
                        {translate('resources.vendors.apiKeys.copy') || 'Copy Credentials'}
                    </Button>
                    <Button
                        onClick={() => setNewKeyDialogOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00C853',
                            '&:hover': {
                                backgroundColor: '#00A843',
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {translate('ra.action.close') || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Revoke Dialog */}
            <Dialog
                open={revokeDialogOpen}
                onClose={() => !revoking && setRevokeDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#D32F2F' }} />
                        {translate('resources.vendors.apiKeys.revokeDialog.title') || 'Revoke OAuth 2.0 Credentials?'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {translate('resources.vendors.apiKeys.revokeDialog.body') ||
                         'Are you sure you want to revoke these OAuth 2.0 credentials? This action cannot be undone. Any integrations using these credentials will stop working immediately.'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setRevokeDialogOpen(false)}
                        disabled={revoking}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('ra.action.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        onClick={confirmRevoke}
                        variant="contained"
                        color="error"
                        disabled={revoking}
                        startIcon={revoking ? <CircularProgress size={16} /> : <Delete />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {revoking
                            ? (translate('resources.vendors.apiKeys.revokeDialog.revoking') || 'Revoking…')
                            : (translate('resources.vendors.apiKeys.revokeDialog.confirm') || 'Revoke')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
